///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// ⌘K command palette: one Spotlight-style search over everything -
/// layers, the entity database (places, satellites, earthquakes,
/// volcanoes), saved views, placemarks and quick actions. Centered in
/// the top third of the screen over a scrim; keyboard-first (arrows,
/// Enter, Escape). With an empty query it offers recent items and the
/// quick actions.
///
/// Split out of the Layers panel so the catalog (a settings list of
/// switches) and search (a palette) no longer share one container. The
/// Layers panel keeps a layer-only filter and hands off here for a global
/// search via openCommandPalette(filterText).
///

import { LayerRow } from "@/components/controls/layers-panel/LayerRow"
import type { CommandItem } from "@/components/controls/layers-panel/commandTypes"
import { LAYERS, type LayerDefinition } from "@/components/controls/layers-panel/layerRegistry"
import { useLayerVisibility } from "@/components/controls/layers-panel/useLayerVisibility"
import { usePlacemarkCommands } from "@/components/controls/layers-panel/usePlacemarkCommands"
import { useSavedViewCommands } from "@/components/controls/layers-panel/useSavedViewCommands"
import { useUnifiedCommands } from "@/components/controls/layers-panel/useUnifiedCommands"
import { useRecentItems } from "@/components/controls/library/useRecentItems"
import { useIsMobile } from "@/components/utils/use-mobile"
import { useGlobalSearchCommands } from "@/hooks/useGlobalSearch"
import { closeCommandPalette, openCommandPalette } from "@/redux/dialogSlice"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { cDefaultPlacemarkColor, upsertPlacemark } from "@/redux/placemarksSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { recordRecentCommand } from "@/redux/userInterfaceSlice"
import { track } from "@/utils/analytics"
import { cn } from "@/utils/utils"
import { Check, Loader2, MapPin, Search, X } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

// Recents shown in the empty state
const cMaxRecentItems = 6

type NavItem =
	| { kind: "layer"; layer: LayerDefinition }
	| { kind: "command"; command: CommandItem }

interface Section {
	label: string
	items: NavItem[]
}

function navItemKey(item: NavItem): string {
	return item.kind === "layer" ? `layer-${item.layer.id}` : item.command.id
}

function matchesQuery(cmd: CommandItem, query: string): boolean {
	if (cmd.enabled === false) return false
	const titleMatch = cmd.title.toLowerCase().includes(query)
	const subtitleMatch = cmd.subtitle?.toLowerCase().includes(query)
	const keywordMatch = cmd.keywords?.some(k => k.toLowerCase().includes(query))
	return !!(titleMatch || subtitleMatch || keywordMatch)
}

/** Launcher pill for the top-left row (beside the Layers button) */
export function SearchButton({ isMobile }: { isMobile: boolean }) {
	const dispatch = useAppDispatch()
	return (
		<button
			onClick={() => dispatch(openCommandPalette())}
			className={cn(
				"flex items-center gap-2 h-9 rounded-xl px-3",
				"bg-black/40 backdrop-blur-xl border border-white/20 shadow-2xl",
				"hover:bg-black/50 hover:border-white/30 active:scale-95",
				"transition-all duration-300 touch-manipulation",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			)}
			aria-label={`Search places, satellites, layers and actions${isMobile ? "" : " (Cmd+K)"}`}
		>
			<Search className="w-4 h-4 text-white/70" />
			<span className="hidden sm:inline text-sm text-white/80">Search</span>
			{!isMobile && (
				<kbd className="px-1.5 py-0.5 bg-white/10 rounded border border-white/20 font-mono text-xs text-white/50">
					⌘K
				</kbd>
			)}
		</button>
	)
}

export function CommandPalette() {
	const dispatch = useAppDispatch()
	const isMobile = useIsMobile()
	const { open: isOpen, initialQuery } = useAppSelector(state => state.dialog.commandPalette)
	const { layerVisible, layerDisabled, toggleLayer, openLayerTable } = useLayerVisibility()
	const staticCommands = useUnifiedCommands()
	const savedViewCommands = useSavedViewCommands()
	const placemarkCommands = usePlacemarkCommands()
	const recents = useRecentItems()

	const [query, setQuery] = useState("")
	const [selectedIndex, setSelectedIndex] = useState(0)
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	// Phones: cap the palette to the visual viewport so the soft keyboard
	// never hides the result list
	const [maxHeight, setMaxHeight] = useState<number | null>(null)

	const trimmedQuery = query.trim()
	const lowerQuery = trimmedQuery.toLowerCase()
	const searching = trimmedQuery.length > 0
	const dbSearchEnabled = isOpen && trimmedQuery.length >= 2
	const searchResults = useGlobalSearchCommands(trimmedQuery, dbSearchEnabled)

	const close = useCallback(() => {
		dispatch(closeCommandPalette())
	}, [dispatch])

	// Seed the query and focus the input on open; reset on close
	useEffect(() => {
		if (isOpen) {
			setQuery(initialQuery)
			setSelectedIndex(0)
			// Next frame so the input exists after the open render
			const id = window.requestAnimationFrame(() => {
				inputRef.current?.focus()
				inputRef.current?.select()
			})
			return () => window.cancelAnimationFrame(id)
		}
		setQuery("")
		setSelectedIndex(0)
	}, [isOpen, initialQuery])

	// Escape closes. Capture phase so surfaces underneath (bottom drawer,
	// layers panel) don't also react.
	useEffect(() => {
		if (!isOpen) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation()
				close()
			}
		}
		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [isOpen, close])

	useEffect(() => {
		if (!isOpen || !isMobile || typeof window === "undefined" || !window.visualViewport) return
		const viewport = window.visualViewport
		const update = () => setMaxHeight(Math.max(160, viewport.height - 24))
		viewport.addEventListener("resize", update)
		viewport.addEventListener("scroll", update)
		update()
		return () => {
			viewport.removeEventListener("resize", update)
			viewport.removeEventListener("scroll", update)
			setMaxHeight(null)
		}
	}, [isOpen, isMobile])

	// Execute a result: record it as recently used (surfaced in the Views
	// menu and the palette's empty state) and close
	const runCommand = useCallback((cmd: CommandItem) => {
		dispatch(recordRecentCommand({
			id: cmd.id,
			type: cmd.type,
			title: cmd.title,
			subtitle: cmd.subtitle,
		}))
		cmd.action()
		close()
	}, [dispatch, close])

	const handleOpenTable = useCallback((layer: LayerDefinition) => {
		if (openLayerTable(layer)) close()
	}, [openLayerTable, close])

	const sections = useMemo((): Section[] => {
		const result: Section[] = []

		if (!searching) {
			// Empty state: what you did last, then the quick actions
			if (recents.items.length > 0) {
				result.push({
					label: "Recent",
					items: recents.items.slice(0, cMaxRecentItems).map(command => ({ kind: "command" as const, command }))
				})
			}
			const quickActions = staticCommands.filter(cmd =>
				cmd.type === "action" && !cmd.id.startsWith("toggle-layer-") && cmd.enabled !== false
			)
			if (quickActions.length > 0) {
				result.push({
					label: "Actions",
					items: quickActions.map(command => ({ kind: "command" as const, command }))
				})
			}
			return result
		}

		// Matching layers first, then personal lists, database results, actions
		const matchingLayers = LAYERS.filter(layer =>
			layer.name.toLowerCase().includes(lowerQuery) || layer.description.toLowerCase().includes(lowerQuery)
		)
		if (matchingLayers.length > 0) {
			result.push({
				label: "Layers",
				items: matchingLayers.map(layer => ({ kind: "layer" as const, layer }))
			})
		}

		// Saved views and places match from the first character: small
		// personal lists, so eager matching keeps the fly-to loop fast
		const matchingViews = savedViewCommands.filter(cmd => matchesQuery(cmd, lowerQuery))
		if (matchingViews.length > 0) {
			result.push({
				label: "Saved Views",
				items: matchingViews.map(command => ({ kind: "command" as const, command }))
			})
		}
		const matchingPlaces = placemarkCommands.filter(cmd => matchesQuery(cmd, lowerQuery))
		if (matchingPlaces.length > 0) {
			result.push({
				label: "My Places",
				items: matchingPlaces.map(command => ({ kind: "command" as const, command }))
			})
		}

		if (trimmedQuery.length >= 2) {
			const dbSections: [string, CommandItem[]][] = [
				["Locations", searchResults.locations],
				["Satellites", searchResults.satellites],
				["Earthquakes", searchResults.earthquakes],
				["Volcanoes", searchResults.volcanoes],
			]
			for (const [label, items] of dbSections) {
				if (items.length > 0) {
					result.push({
						label,
						items: items.map(command => ({ kind: "command" as const, command }))
					})
				}
			}

			const matchingActions = staticCommands.filter(cmd => matchesQuery(cmd, lowerQuery))
			if (matchingActions.length > 0) {
				result.push({
					label: "Actions",
					items: matchingActions.map(command => ({ kind: "command" as const, command }))
				})
			}
		}

		return result
	}, [searching, lowerQuery, trimmedQuery, searchResults, staticCommands, savedViewCommands, placemarkCommands, recents.items])

	// Flattened list matching display order, for keyboard navigation
	const navItems = useMemo(() => sections.flatMap(section => section.items), [sections])

	useEffect(() => {
		setSelectedIndex(0)
	}, [trimmedQuery])

	// Enter activates the selected item: open its browser when it has one,
	// otherwise toggle its visibility
	const activateNavItem = useCallback((item: NavItem) => {
		if (trimmedQuery) {
			track("search_used", { result_kind: item.kind, had_result: true })
		}
		if (item.kind === "command") {
			runCommand(item.command)
		} else if (layerDisabled(item.layer)) {
			return
		} else if (item.layer.commandId) {
			handleOpenTable(item.layer)
		} else if (item.layer.visibility) {
			toggleLayer(item.layer.visibility, !layerVisible(item.layer.visibility))
		}
	}, [trimmedQuery, runCommand, handleOpenTable, toggleLayer, layerVisible, layerDisabled])

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		// ⌘K inside the palette's own input closes it (the global shortcut
		// ignores keystrokes from inputs)
		if ((e.metaKey || e.ctrlKey) && e.key === "k") {
			e.preventDefault()
			close()
			return
		}
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault()
				setSelectedIndex(prev => Math.min(prev + 1, navItems.length - 1))
				break
			case "ArrowUp":
				e.preventDefault()
				setSelectedIndex(prev => Math.max(prev - 1, 0))
				break
			case "Enter":
				e.preventDefault()
				if (navItems[selectedIndex]) {
					activateNavItem(navItems[selectedIndex])
				}
				break
		}
	}, [navItems, selectedIndex, activateNavItem, close])

	useEffect(() => {
		if (!listRef.current) return
		const selectedElement = listRef.current.querySelector(`[data-nav-index="${selectedIndex}"]`)
		selectedElement?.scrollIntoView({ block: "nearest" })
	}, [selectedIndex])

	if (!isOpen) return null

	const noResults = searching && !searchResults.loading && navItems.length === 0

	return (
		<div className="fixed inset-0 z-[9000]" role="dialog" aria-modal="true" aria-label="Search">
			{/* Scrim */}
			<div className="absolute inset-0 bg-black/40" onClick={close} aria-hidden="true" />

			<div
				className={cn(
					"absolute left-1/2 -translate-x-1/2 flex flex-col overflow-hidden",
					"w-[min(600px,calc(100vw-1rem))] max-h-[min(70vh,560px)]",
					"top-[max(0.5rem,env(safe-area-inset-top,0px))] sm:top-[12vh]",
					"bg-black/50 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl"
				)}
				style={maxHeight !== null ? { maxHeight } : undefined}
				onKeyDown={handleKeyDown}
				data-command-palette
			>
				<div className="flex items-center gap-2.5 px-3.5 h-12 border-b border-white/10 flex-shrink-0">
					<Search className="h-4 w-4 text-white/50 flex-shrink-0" />
					<input
						ref={inputRef}
						value={query}
						onChange={e => setQuery(e.target.value)}
						placeholder="Search places, satellites, layers, actions…"
						className="flex-1 min-w-0 bg-transparent text-base sm:text-sm text-white placeholder:text-white/40 focus:outline-none"
						aria-label="Search"
						autoComplete="off"
						autoCorrect="off"
						spellCheck={false}
					/>
					{searchResults.loading && (
						<Loader2 className="h-4 w-4 text-cyan-400 animate-spin flex-shrink-0" />
					)}
					{isMobile ? (
						<button
							onClick={close}
							className="h-7 w-7 -mr-1 flex items-center justify-center rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-colors"
							aria-label="Close search"
						>
							<X className="h-4 w-4" />
						</button>
					) : (
						<kbd className="px-1.5 py-0.5 bg-white/10 rounded border border-white/20 font-mono text-[10px] text-white/50">
							esc
						</kbd>
					)}
				</div>

				<div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-1.5 pb-1.5">
					{(() => {
						let globalIndex = 0
						return sections.map(section => (
							<div key={section.label}>
								<div className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
									{section.label}
								</div>
								{section.items.map(item => {
									const currentIndex = globalIndex++
									const isSelected = currentIndex === selectedIndex

									if (item.kind === "layer") {
										return (
											<LayerRow
												key={navItemKey(item)}
												layer={item.layer}
												visible={item.layer.visibility ? layerVisible(item.layer.visibility) : false}
												disabled={layerDisabled(item.layer)}
												isMobile={isMobile}
												navIndex={currentIndex}
												isSelected={isSelected}
												showDescription
												onToggle={toggleLayer}
												onOpenTable={handleOpenTable}
												onHover={() => setSelectedIndex(currentIndex)}
											/>
										)
									}

									return (
										<CommandRow
											key={navItemKey(item)}
											command={item.command}
											isMobile={isMobile}
											navIndex={currentIndex}
											isSelected={isSelected}
											onClick={() => activateNavItem(item)}
											onHover={() => setSelectedIndex(currentIndex)}
										/>
									)
								})}
							</div>
						))
					})()}
					{noResults && (
						<div className="px-3 py-8 text-center text-sm text-slate-500">
							No results for “{trimmedQuery}”
						</div>
					)}
					{!searching && navItems.length === 0 && (
						<div className="px-3 py-8 text-center text-sm text-slate-500">
							Type to search places, satellites, earthquakes, volcanoes, layers and actions
						</div>
					)}
				</div>

				{!isMobile && (
					<div className="flex items-center gap-3 px-3.5 py-1.5 border-t border-white/10 text-[10px] text-white/35 flex-shrink-0">
						<span><kbd className="font-mono">↑↓</kbd> navigate</span>
						<span><kbd className="font-mono">↵</kbd> open</span>
						<span><kbd className="font-mono">esc</kbd> close</span>
					</div>
				)}
			</div>
		</div>
	)
}

interface CommandRowProps {
	command: CommandItem
	isMobile: boolean
	navIndex: number
	isSelected: boolean
	onClick: () => void
	onHover: () => void
}

function CommandRow({ command, isMobile, navIndex, isSelected, onClick, onHover }: CommandRowProps) {
	const dispatch = useAppDispatch()
	const [imageError, setImageError] = useState(false)
	const [placeAdded, setPlaceAdded] = useState(false)

	// Inline "Add placemark" for place results (spec: a place search result
	// offers Add placemark alongside fly-to). Row click still flies.
	const handleAddPlace = useCallback((event: React.MouseEvent) => {
		event.stopPropagation()
		if (placeAdded || !command.place) return
		const now = new Date().toISOString()
		dispatch(upsertPlacemark({
			id: crypto.randomUUID(),
			name: command.place.name,
			locations: [{ id: crypto.randomUUID(), lat: command.place.lat, lon: command.place.lon }],
			color: cDefaultPlacemarkColor,
			tags: [],
			createdAt: now,
			updatedAt: now,
		}))
		dispatch(sendDatasetAction({ type: "placemark", action: "enable" }))
		setPlaceAdded(true)
	}, [dispatch, command.place, placeAdded])

	return (
		<button
			data-nav-index={navIndex}
			onClick={onClick}
			onMouseEnter={onHover}
			className={cn(
				"w-full flex items-center gap-2.5 rounded-lg px-2 transition-colors text-left group/cmdrow",
				isMobile ? "py-2" : "py-1.5",
				"hover:bg-white/5 active:bg-white/10",
				isSelected && "bg-white/10"
			)}
		>
			<div className="flex items-center justify-center p-1 rounded-lg bg-slate-500/10 border border-slate-500/20 flex-shrink-0">
				{command.icon}
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-white truncate">{command.title}</div>
				{command.subtitle && (
					<div className="text-xs text-slate-500 truncate">{command.subtitle}</div>
				)}
			</div>
			{command.place && (
				<span
					role="button"
					tabIndex={-1}
					onClick={handleAddPlace}
					title={placeAdded ? "Place saved" : "Add placemark"}
					aria-label={placeAdded ? `${command.title} saved to My Places` : `Add placemark for ${command.title}`}
					className={cn(
						"flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md transition-all",
						placeAdded
							? "text-emerald-400"
							: cn(
								"text-white/40 hover:text-rose-300 hover:bg-white/10",
								!isMobile && "opacity-0 group-hover/cmdrow:opacity-100 focus-visible:opacity-100"
							),
						isSelected && "opacity-100"
					)}
				>
					{placeAdded ? <Check className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
				</span>
			)}
			{command.image_url && !imageError && (
				<div className="flex-shrink-0 h-5 w-auto rounded overflow-hidden bg-white/5 border border-white/10">
					<img
						src={command.image_url}
						alt=""
						width={40}
						height={20}
						className="h-5 w-auto object-contain"
						onError={() => setImageError(true)}
					/>
				</div>
			)}
		</button>
	)
}
